import React, { useState } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../Appstore/store";
import { Addagentsmonitors } from "../Features/Getallagentsmonitors";
import { BiShow, BiHide } from "react-icons/bi";
import { IoMdArrowRoundBack } from "react-icons/io";
import { toast } from "sonner";

const validationSchema = Yup.object({
  name: Yup.string()
    .min(3, "Name must be at least 3 characters")
    .max(250, "Name must be at most 250 characters")
    .required("Name is required")
    .trim(),
  email: Yup.string().email("Invalid email").required("Email is required"),
  phoneNumber: Yup.string()
    .matches(
      /^\+1\d{10}$/,
      "Invalid phone number format. Must start with +1 and contain 10 digits without any spaces."
    ),
  agentmonitors: Yup.string().required("Please select an option"),
  password: Yup.string()
    .min(8, "Password must be at least 8 characters")
    .matches(/[a-z]/, "Password must contain at least one lowercase letter")
    .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
    .matches(/\d/, "Password must contain at least one number")
    .required("Password is required"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("password"), undefined], "Passwords do not match")
    .required("Confirm password is required"),
  status: Yup.string().required("Status is required"),
});

interface AgentMonitor {
  id?: number;
  name: string;
  email: string;
  phone_number: string;
  role: number;
  status: number;
  password: string;
  confirm_password: string;
  createdAt?: string;
  updatedAt?: string;
}

function Addrecord() {
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] =
    useState<boolean>(false);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const isActive = (path: string) =>
    location.pathname === path ? "active" : "";

  const initialValues = {
    name: "",
    email: "",
    phoneNumber: "",
    password: "",
    confirmPassword: "",
    status: "Active",
    agentmonitors: "agents",
  };

  const navigatetobackpage = () => {
    navigate(-1);
  };

  return (
    <>
      <div className="wrapper">
        <div className="content-wrapper">
          <section className="content">
            <div className="container-fluid">
              <div className="card mt-4">
                <div className="card-header bg-white p-3 d-flex justify-content-md-between">
                  <h2 className="w-100 mb-md-0 fs-3">Add User</h2>
                  <Link to={""} className="text-decoration-none ">
                    <button
                      onClick={navigatetobackpage}
                      className="btn btn-info text-white d-flex align-items-center fw-semibold"
                    >
                      <IoMdArrowRoundBack /> Back
                    </button>
                  </Link>
                </div>
                <div className="card-body">
                  <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={async (values, { setSubmitting }) => {
                      const agentMonitorData: AgentMonitor = {
                        name: values.name,
                        email: values.email,
                        phone_number: values.phoneNumber,
                        role: values.agentmonitors === "agents" ? 1 : 2,
                        status: values.status === "Active" ? 1 : 0,
                        password: values.password,
                        confirm_password: values.confirmPassword,
                      };

                      try {
                        const response: any = await dispatch(
                          Addagentsmonitors(agentMonitorData)
                        );

                        if (
                          response.payload &&
                          typeof response.payload === "object"
                        ) {
                          if (response?.payload?.status === "success") {
                            toast.success(response?.payload?.message);
                            navigate("/admin/users");
                          } else {
                            toast.error(response?.payload?.message);
                          }
                        }
                      } finally {
                        setSubmitting(false);
                      }
                    }}
                  >
                    {({
                      values,
                      errors,
                      touched,
                      handleChange,
                      handleBlur,
                      isSubmitting,
                    }) => (
                      <div>
                        <Form>
                          <div className="row">
                            <div className="form-group mb-3 col-md-6">
                              <label htmlFor="name">
                                Name<span className="text-danger">*</span>
                              </label>
                              <input
                                type="text"
                                className={`form-control shadow-none ${touched.name && errors.name
                                  ? "is-invalid"
                                  : ""
                                  }`}
                                id="name"
                                name="name"
                                placeholder="Enter Name"
                                value={values.name}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                              {touched.name && errors.name && (
                                <div className="text-danger">{errors.name}</div>
                              )}
                            </div>

                            <div className="form-group mb-3 col-md-6">
                              <label>
                                Role<span className="text-danger">*</span>
                              </label>
                              <select
                                name="agentmonitors"
                                value={values.agentmonitors}
                                onChange={handleChange}
                                className="form-select shadow-none"
                              >
                                <option value="agents">Agents</option>
                                <option value="monitors">Monitors</option>
                              </select>
                              {touched.agentmonitors &&
                                errors.agentmonitors && (
                                  <div className="text-danger d-block">
                                    {errors.agentmonitors}
                                  </div>
                                )}
                            </div>
                          </div>

                          <div className="row">
                            <div className="form-group mb-3 col-md-6">
                              <label htmlFor="email">
                                Email<span className="text-danger">*</span>
                              </label>
                              <input
                                type="email"
                                className={`form-control shadow-none ${touched.email && errors.email
                                  ? "is-invalid"
                                  : ""
                                  }`}
                                id="email"
                                name="email"
                                placeholder="Enter Email"
                                value={values.email}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                              {touched.email && errors.email && (
                                <div className="text-danger">
                                  {errors.email}
                                </div>
                              )}
                            </div>

                            <div className="form-group mb-3 col-md-6">
                              <label htmlFor="phoneNumber">
                                Phone Number
                                <span className="text-danger">*</span>
                              </label>
                              <input
                                type="text"
                                className={`form-control shadow-none ${touched.phoneNumber && errors.phoneNumber
                                  ? "is-invalid"
                                  : ""
                                  }`}
                                id="phoneNumber"
                                name="phoneNumber"
                                placeholder="Enter Phone Number"
                                value={values.phoneNumber}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                              {touched.phoneNumber && errors.phoneNumber && (
                                <div className="text-danger">
                                  {errors.phoneNumber}
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="row">
                            <div className="form-group mb-3 col-md-6">
                              <label htmlFor="password">
                                Password<span className="text-danger">*</span>
                              </label>
                              <div className="position-relative">
                                <Field
                                  type={showPassword ? "text" : "password"}
                                  name="password"
                                  className={`form-control shadow-none ${touched.password && errors.password
                                    ? "is-invalid"
                                    : ""
                                    }`}
                                  placeholder="Enter Password"
                                />
                                <button
                                  type="button"
                                  className="btn btn-light position-absolute"
                                  onClick={() => setShowPassword(!showPassword)}
                                  style={{
                                    top: "50%",
                                    right: "10px",
                                    transform: "translateY(-50%)",
                                    border: "none",
                                    background: "none",
                                  }}
                                >
                                  {showPassword ? <BiShow /> : <BiHide />}
                                </button>
                              </div>
                              {touched.password && errors.password && (
                                <div className="text-danger">
                                  {errors.password}
                                </div>
                              )}
                            </div>

                            <div className="form-group mb-3 col-md-6">
                              <label htmlFor="confirmPassword">
                                Confirm Password
                                <span className="text-danger">*</span>
                              </label>
                              <div className="position-relative">
                                <Field
                                  type={
                                    showConfirmPassword ? "text" : "password"
                                  }
                                  name="confirmPassword"
                                  className={`form-control shadow-none ${touched.confirmPassword &&
                                    errors.confirmPassword
                                    ? "is-invalid"
                                    : ""
                                    }`}
                                  placeholder="Enter Confirm Password"
                                />
                                <button
                                  type="button"
                                  className="btn btn-light position-absolute"
                                  onClick={() =>
                                    setShowConfirmPassword(!showConfirmPassword)
                                  }
                                  style={{
                                    top: "50%",
                                    right: "10px",
                                    transform: "translateY(-50%)",
                                    border: "none",
                                    background: "none",
                                  }}
                                >
                                  {showConfirmPassword ? (
                                    <BiShow />
                                  ) : (
                                    <BiHide />
                                  )}
                                </button>
                              </div>
                              {touched.confirmPassword &&
                                errors.confirmPassword && (
                                  <div className="text-danger">
                                    {errors.confirmPassword}
                                  </div>
                                )}
                            </div>
                          </div>

                          <div className="form-group mb-3 col-md-6">
                            <label>
                              Status<span className="text-danger">*</span>
                            </label>
                            <div className="d-flex align-items-center">
                              <input
                                type="radio"
                                id="active"
                                name="status"
                                value="Active"
                                checked={values.status === "Active"}
                                onChange={handleChange}
                              />
                              <label htmlFor="active" className="ms-1 mb-0">
                                Active
                              </label>

                              <input
                                type="radio"
                                id="deactive"
                                name="status"
                                value="Deactive"
                                checked={values.status === "Deactive"}
                                onChange={handleChange}
                                className="ms-3"
                              />
                              <label htmlFor="deactive" className="ms-1 mb-0">
                                Deactive
                              </label>
                            </div>
                            {touched.status && errors.status && (
                              <div className="text-danger">{errors.status}</div>
                            )}
                          </div>

                          <div className="d-flex">
                            <button
                              type="submit"
                              className="btn btn-primary me-2"
                              disabled={isSubmitting}
                            >
                              {isSubmitting ? (
                                <>
                                  <span
                                    className="spinner-border spinner-border-sm"
                                    role="status"
                                    aria-hidden="true"
                                  ></span>
                                  <span className="ps-2">Submitting...</span>
                                </>
                              ) : (
                                "Submit"
                              )}
                            </button>
                            <Link
                              to="/admin/users"
                              className={`nav-link ${isActive("/admin/users")}`}
                            >
                              <button className="btn btn-secondary">
                                Cancel
                              </button>
                            </Link>
                          </div>
                        </Form>
                      </div>
                    )}
                  </Formik>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
}

export default Addrecord;
